import { FC, useMemo, useState } from 'react';

import clickmeVideoPortfolioShowAllButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/clickme_video_portfolio_show_all_button_click';
import { Button, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import { ChevronDownOutlinedSize24, ChevronUpOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { BannerVideoModal } from 'components/common/BannerVideo/BannerVideoModal/BannerVideoModal';
import { VideoPreviewItem } from 'components/common/BannerVideo/VideoGallery/VideoPreviewItem/VideoPreviewItem';

import type { VideoGalleryItem } from 'store/models/videoGallery';
import { selectedVideoGalleryUploadId, selectVideoGalleryItems } from 'store/models/videoGallery';
import { useSelector } from 'store/utils';

import styles from 'components/common/BannerVideo/VideoGallery/VideoGallery.module.less';

const COLLAPSED_COUNT_DESKTOP = 3;
const COLLAPSED_COUNT_MOBILE = 4;

const TrlKeys = {
    title: 'AdCreate.AdForm.BannerVideo.VideoGallery.title',
    expand: 'AdCreate.AdForm.BannerVideo.VideoGallery.expand',
    collapse: 'AdCreate.AdForm.BannerVideo.VideoGallery.collapse',
};

export const VideoGallery: FC = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isModalVisible, setIsModalVisibleOn, setIsModalVisibleOff] = useOnOffState(false);
    const [modalVideo, setModalVideo] = useState<VideoGalleryItem | null>(null);

    const trls = useTrls();
    const { isXS } = useBreakpoint();

    const videos = useSelector(selectVideoGalleryItems);
    const selectedId = useSelector(selectedVideoGalleryUploadId);

    const handleVideoClick = (video: VideoGalleryItem) => {
        setModalVideo(video);
        setIsModalVisibleOn();
    };

    const handleCloseModal = () => {
        setIsModalVisibleOff();
        setModalVideo(null);
    };

    const collapsedCount = isXS ? COLLAPSED_COUNT_MOBILE : COLLAPSED_COUNT_DESKTOP;
    const hasMore = videos.length > collapsedCount;

    const visibleVideos = useMemo(
        () => (isExpanded ? videos : videos.slice(0, collapsedCount)),
        [isExpanded, videos, collapsedCount]
    );

    const handleExpand = () => {
        if (!isExpanded) {
            clickmeVideoPortfolioShowAllButtonClick();
        }
        setIsExpanded((prev) => !prev);
    };

    return (
        <>
            <Text typography="label-2-regular" style="secondary">
                {trls(TrlKeys.title)}
            </Text>
            <VSpacing default={8} />
            <div className={styles.grid}>
                {visibleVideos.map((video) => (
                    <VideoPreviewItem
                        key={video.videoUploadId}
                        video={video}
                        isSelected={video.videoUploadId === selectedId}
                        onClick={() => handleVideoClick(video)}
                    />
                ))}
            </div>
            {hasMore && (
                <>
                    <VSpacing default={12} />
                    <Button
                        mode="secondary"
                        stretched
                        style="neutral"
                        size="small"
                        spaceBetween={false}
                        icon={isExpanded ? <ChevronUpOutlinedSize24 /> : <ChevronDownOutlinedSize24 />}
                        onClick={handleExpand}
                        data-qa="video-gallery-expand-button"
                    >
                        {trls(isExpanded ? TrlKeys.collapse : TrlKeys.expand)}
                    </Button>
                </>
            )}
            <BannerVideoModal
                visible={isModalVisible}
                onClose={handleCloseModal}
                videoUrl={modalVideo?.videoUrl ?? ''}
                thumbnailUrl={modalVideo?.thumbnailUrl ?? ''}
                height={modalVideo?.height ?? 0}
                width={modalVideo?.width ?? 0}
            />
        </>
    );
};
